import React, { useEffect } from 'react';
import { colors } from '../styles/variables';
import CourseHeader from '../components/course/CourseHeader';
import CourseAbout from '../components/course/CourseAbout';
import CourseCurriculum from '../components/course/CourseCurriculum';
import CourseProjects from '../components/course/CourseProjects';
import CourseInstructors from '../components/course/CourseInstructors';
import CourseCta from '../components/course/CourseCta';
import CourseReviewSection from '../components/course/CourseReviewSection';
import Seo from '../components/common/Seo';
import { projectsByCourse } from '../data/projects';

const courseJsonLd = {
  '@context': 'https://schema.org',
  '@type': 'Course',
  name: 'TypeScript',
  description: '안정성과 생산성을 높이는 TypeScript 실전 활용법을 1:1 멘토링으로 학습합니다.',
  provider: {
    '@type': 'EducationalOrganization',
    name: 'ElectroWave Club',
    sameAs: 'https://electrowave.kr',
  },
  inLanguage: 'ko',
  educationalLevel: 'intermediate',
  url: 'https://electrowave.kr/typescript',
};

const TypeScriptPage = () => {
  useEffect(() => { window.scrollTo(0, 0); }, []);
  // 코스 헤더 데이터
  const headerData = {
    tag: '타입 안전성',
    title: 'TypeScript 과정',
    description: 'JavaScript에 정적 타입 시스템을 추가하여 더 안정적이고 유지보수가 용이한 코드를 작성하는 방법을 배웁니다. 실무에서 활용되는 TypeScript의 핵심 개념과 React와의 통합 방법을 익혀보세요.',
    duration: '총 32시간 / 8주 과정',
    level: '중급자 대상',
    bgColor: colors.typescriptColor,
    bgImage: '/assets/images/course-thumb-typescript.png'
  };
  
  // 코스 소개 데이터
  const aboutData = {
    subtitle: 'JavaScript의 유연함에 타입 시스템의 안정성을 더한 TypeScript의 핵심 개념부터 실전 활용법까지 배우는 과정입니다.',
    forWhom: [
      'JavaScript를 사용해본 경험이 있는 개발자',
      '코드의 안정성과 유지보수성을 높이고 싶은 분',
      'React, Vue 등의 프레임워크와 TypeScript를 함께 활용하고 싶은 분',
      '프론트엔드 개발 직무로 취업을 준비하는 분',
      '대규모 애플리케이션 개발에 필요한 타입 시스템을 배우고 싶은 분'
    ],
    skills: [
      'TypeScript의 기본 문법과 타입 시스템 이해',
      '인터페이스, 제네릭, 유틸리티 타입 활용 능력',
      'React와 TypeScript를 결합한 개발 역량',
      '타입 안전한 코드 작성 및 디버깅 능력',
      '기존 JavaScript 프로젝트를 TypeScript로 마이그레이션하는 방법'
    ],
    imageSrc: '/assets/images/course-thumb-typescript.png'
  };
  
  // 커리큘럼 데이터
  const curriculumData = {
    subtitle: 'JavaScript 개발자가 TypeScript로 쉽게 전환할 수 있도록 단계적으로 설계된 커리큘럼입니다.',
    modules: [
      {
        title: '1. TypeScript 소개 및 개발 환경 설정',
        description: 'TypeScript의 개념과 필요성을 이해하고 개발 환경을 구축합니다.',
        lessons: [
          'TypeScript의 역사와 필요성',
          'TypeScript vs JavaScript 비교',
          'Node.js 및 TypeScript 설치',
          'tsconfig.json 설정'
        ]
      },
      {
        title: '2. TypeScript 기본 타입',
        description: 'TypeScript의 기본 타입 시스템과 변수 선언 방법을 학습합니다.',
        lessons: [
          '기본 타입: boolean, number, string, array',
          '특수 타입: any, unknown, never, void',
          '타입 추론과 타입 단언',
          '유니온 타입과 인터섹션 타입'
        ]
      },
      {
        title: '3. 인터페이스와 타입 별칭',
        description: '복잡한 타입을 정의하고 재사용하는 방법을 배웁니다.',
        lessons: [
          '인터페이스 정의와 활용',
          '선택적 프로퍼티와 읽기 전용 프로퍼티',
          '타입 별칭(Type Alias) 사용법',
          '인터페이스 확장과 병합'
        ]
      },
      {
        title: '4. 함수와 클래스',
        description: 'TypeScript에서 함수와 클래스를 정의하고 활용하는 방법을 학습합니다.',
        lessons: [
          '함수 매개변수와 반환 타입',
          '선택적 매개변수와 기본 매개변수',
          '클래스와 접근 제어자',
          '추상 클래스와 인터페이스 구현'
        ]
      },
      {
        title: '5. 제네릭 프로그래밍',
        description: '재사용 가능한 유연한 코드를 작성하기 위한 제네릭 프로그래밍 방법을 배웁니다.',
        lessons: [
          '제네릭 함수 작성',
          '제네릭 인터페이스와 클래스',
          '제약 조건과 기본 타입',
          '유틸리티 타입 활용 (Partial, Readonly, Record 등)'
        ]
      },
      {
        title: '6. TypeScript와 모듈',
        description: '모듈화된 코드 작성 방법과 네임스페이스 활용 기법을 학습합니다.',
        lessons: [
          'Export와 Import 문법',
          '네임스페이스와 모듈',
          'Ambient 모듈과 Declaration 파일(.d.ts)',
          '서드파티 라이브러리 타입 정의 활용'
        ]
      },
      {
        title: '7. React와 TypeScript',
        description: 'React 프로젝트에서 TypeScript를 활용하는 방법을 배웁니다.',
        lessons: [
          'Create React App에 TypeScript 적용',
          '컴포넌트와 Props 타입 정의',
          'useState, useEffect와 타입',
          '커스텀 훅 작성과 타입 정의'
        ]
      },
      {
        title: '8. 프로젝트: TypeScript 기반 웹 애플리케이션 개발',
        description: '학습한 내용을 종합적으로 적용하여 TypeScript 기반의 웹 애플리케이션을 개발합니다.',
        lessons: [
          '프로젝트 요구사항 분석 및 설계',
          'TypeScript와 React를 활용한 UI 컴포넌트 구현',
          '상태 관리와 API 연동',
          '테스트 및 디버깅',
          '프로젝트 완성 및 발표'
        ]
      }
    ]
  };
  
  // 프로젝트 데이터
  const projectsData = {
    subtitle: '수업에서 배운 내용을 바로 적용할 수 있는 실전형 TypeScript 프로젝트를 수행합니다.',
    projects: projectsByCourse.typescript,
  };
  
  // 강사진 데이터
  const instructorsData = {
    subtitle: 'TypeScript와 프론트엔드 개발 분야의 전문가들이 여러분의 성장을 돕습니다.',
    instructors: [
      {
        name: 'Lisa Choi',
        title: '프론트엔드 아키텍트',
        bio: '대형 숙박 플랫폼 프론트엔드를 TypeScript로 운영한 경험이 풍부하며, 타입 시스템 설계와 대규모 코드베이스의 점진적 마이그레이션 분야에서 전문성을 보유하고 있습니다. 실무에서 바로 통하는 타입 안전 개발 패턴을 가르칩니다.',
        image: '/assets/images/instructor-choi-yujin.png'
      }
    ]
  };
  
  // CTA 데이터
  const ctaData = {
    title: 'TypeScript로 안정적인 웹 개발을 시작하세요',
    subtitle: 'ElectroWave Club의 TypeScript 과정을 통해 타입 안전성이 보장된 웹 애플리케이션을 개발할 수 있는 전문가로 성장하세요.',
    bgColor: colors.typescriptColor,
    endColor: '#01579B'  // 진한 하늘색
  };
  
  return (
    <>
      <Seo
        title="TypeScript"
        description="안정성과 생산성을 높이는 TypeScript 실전 활용법을 1:1 멘토링으로 학습합니다."
        path="/typescript"
        ogImage="/assets/images/course-thumb-typescript.png"
        ogType="article"
        jsonLd={courseJsonLd}
      />
      <CourseHeader {...headerData} />
      <CourseAbout {...aboutData} />
      <CourseCurriculum {...curriculumData} />
      <CourseProjects {...projectsData} courseSlug="typescript" />
      <CourseInstructors {...instructorsData} />
      <CourseReviewSection courseTitle="TypeScript" />
      <CourseCta {...ctaData} />
    </>
  );
};

export default TypeScriptPage;
